/**
 * Live ticket QR, drawn in the browser (16 Oct 2026).
 *
 * The server gives the device holding a ticket a per-ticket display key. From
 * it this file makes, offline:
 *   a QR that changes every 15 seconds   v2.<ticketId>.<step>.<mac>
 *   a 6-digit code that changes every minute, read out with the serial when
 *   the QR will not scan
 * The gate sends what it reads to the server, which derives the same key and
 * checks the code is current, so a screenshot is useless within a minute.
 *
 * A copy of the apps' app/utility/liveTicket.js. The algorithm is pinned
 * against the server (Lusuku_Dashboard_Node-Server
 * services/ticketing/liveTicketCredential.js) and both apps by shared test
 * vectors (src/utils/liveTicket.test.js). Change all of them together.
 *
 * Pure JavaScript SHA-256 and HMAC on purpose: synchronous (WebCrypto is not),
 * no dependency, and the same code the apps run.
 */

// ── SHA-256 / HMAC-SHA256 (FIPS 180-4, RFC 2104) ─────────────────────────────
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x, n) => (x >>> n) | (x << (32 - n));

export function sha256(bytes) {
  const len = bytes.length;
  const withPadding = ((len + 9 + 63) >> 6) << 6;
  const buf = new Uint8Array(withPadding);
  buf.set(bytes);
  buf[len] = 0x80;
  const bitLen = len * 8;
  const view = new DataView(buf.buffer);
  view.setUint32(withPadding - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(withPadding - 4, bitLen >>> 0);

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  for (let off = 0; off < withPadding; off += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0;
    h[1] = (h[1] + b) >>> 0;
    h[2] = (h[2] + c) >>> 0;
    h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0;
    h[5] = (h[5] + f) >>> 0;
    h[6] = (h[6] + g) >>> 0;
    h[7] = (h[7] + hh) >>> 0;
  }
  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  for (let i = 0; i < 8; i += 1) outView.setUint32(i * 4, h[i]);
  return out;
}

export function hmacSha256(keyBytes, messageBytes) {
  let key = keyBytes;
  if (key.length > 64) key = sha256(key);
  const block = new Uint8Array(64);
  block.set(key);
  const inner = new Uint8Array(64 + messageBytes.length);
  const outer = new Uint8Array(64 + 32);
  for (let i = 0; i < 64; i += 1) {
    inner[i] = block[i] ^ 0x36;
    outer[i] = block[i] ^ 0x5c;
  }
  inner.set(messageBytes, 64);
  outer.set(sha256(inner), 64);
  return sha256(outer);
}

// ── Encodings ────────────────────────────────────────────────────────────────
export function utf8(text) {
  const out = [];
  const s = String(text);
  for (let i = 0; i < s.length; i += 1) {
    let c = s.charCodeAt(i);
    if (c >= 0xd800 && c < 0xdc00 && i + 1 < s.length) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next < 0xe000) {
        c = 0x10000 + ((c - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      }
    }
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

export function toBase64Url(bytes) {
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
  }
  if (i < bytes.length) {
    const rest = bytes.length - i;
    const n = (bytes[i] << 16) | (rest > 1 ? bytes[i + 1] << 8 : 0);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
    if (rest > 1) out += B64[(n >> 6) & 63];
  }
  return out;
}

export function fromBase64Url(text) {
  const clean = String(text || "").replace(/=+$/, "");
  const out = [];
  let bits = 0;
  let value = 0;
  for (let i = 0; i < clean.length; i += 1) {
    const ch = clean[i] === "+" ? "-" : clean[i] === "/" ? "_" : clean[i];
    const idx = B64.indexOf(ch);
    if (idx < 0) throw new Error("Invalid base64url");
    value = (value << 6) | idx;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((value >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}

// ── The live credential ──────────────────────────────────────────────────────
export const QR_STEP_SECONDS = 15;
export const CODE_STEP_SECONDS = 60;

export const qrStepAt = (nowMs, stepSeconds = QR_STEP_SECONDS) => Math.floor(nowMs / 1000 / stepSeconds);
export const codeStepAt = (nowMs, codeSeconds = CODE_STEP_SECONDS) => Math.floor(nowMs / 1000 / codeSeconds);

export function liveMac(keyBytes, ticketId, step) {
  return toBase64Url(hmacSha256(keyBytes, utf8(`qr|${ticketId}|${step}`)).subarray(0, 12));
}

export function liveCodeForStep(keyBytes, ticketId, codeStep) {
  const h = hmacSha256(keyBytes, utf8(`code|${ticketId}|${codeStep}`));
  const o = h[h.length - 1] & 0x0f;
  const bin = ((h[o] & 0x7f) << 24) | (h[o + 1] << 16) | (h[o + 2] << 8) | h[o + 3];
  return String(bin % 1000000).padStart(6, "0");
}

/**
 * Everything the ticket screen draws for one moment.
 * @param {{ key: string, stepSeconds?: number, codeSeconds?: number, offsetMs?: number }} live
 * @param {string} ticketId
 * @param {number} [deviceNowMs]
 */
export function liveFrame(live, ticketId, deviceNowMs = Date.now()) {
  const keyBytes = fromBase64Url(live.key);
  const stepSeconds = Number(live.stepSeconds) || QR_STEP_SECONDS;
  const codeSeconds = Number(live.codeSeconds) || CODE_STEP_SECONDS;
  // Server time, as best this phone knows it: a phone whose clock is minutes
  // off would otherwise draw codes the gate refuses.
  const nowMs = deviceNowMs + (Number(live.offsetMs) || 0);
  const step = qrStepAt(nowMs, stepSeconds);
  const codeStep = codeStepAt(nowMs, codeSeconds);
  const elapsedInStep = (nowMs / 1000) % stepSeconds;
  return {
    token: `v2.${ticketId}.${step}.${liveMac(keyBytes, ticketId, step)}`,
    code: liveCodeForStep(keyBytes, ticketId, codeStep),
    step,
    secondsLeft: Math.max(0, Math.ceil(stepSeconds - elapsedInStep)),
    fractionLeft: Math.max(0, Math.min(1, 1 - elapsedInStep / stepSeconds)),
    nowMs,
  };
}

/** "482913" as "482 913", the way it is read out. */
export const formatLiveCode = (code) =>
  /^\d{6}$/.test(String(code || "")) ? `${code.slice(0, 3)} ${code.slice(3)}` : "";
